import { ChevronDown20 } from '@carbon/icons-react';
import { Listbox } from '@headlessui/react';
import clsx from 'clsx';
import React, { useEffect, useState } from 'react';

import InfoIconBox from '../infoIconBox';

export type DropdownItemType = {
  value: string | number;
  text: string;
};

export type DropdownChangeEventProps = {
  selectedIndex: number;
  value?: string | number;
  text?: string | number;
};

export interface DropdownProps {
  items: string[] | number[] | DropdownItemType[];
  label?: string;
  darkBg?: boolean;
  value?: string | number;
  disabled?: boolean;
  hasInfoIcon?: boolean;
  infoText?: string;
  hasGreyBg?: boolean;
  labelPosition?: 'left' | 'top';
  context?: string;
  onChange?: ({ selectedIndex, value, text }: DropdownChangeEventProps) => void;
  whiteLabel?: boolean;
  tradeInWidget?: boolean;
}

export const getIndexForValue = (
  items: any[],
  value: string | number
): number => {
  for (let index = 0; index < items.length; index++) {
    const item = items[index];
    if (getItemContent(item, 'value') === value) {
      return index;
    }
  }

  const parsedValue = parseInt(value.toString(), 10);
  if (!isNaN(parsedValue) && parsedValue < items.length) return parsedValue;

  return 0;
};

export const getItemContent = (
  item: string | number | DropdownItemType,
  contentType: 'value' | 'text'
): string | number | undefined => {
  let res = undefined;

  const isSimple = typeof item === 'string' || typeof item === 'number';
  const isComplex =
    !isSimple &&
    typeof item?.value !== undefined &&
    typeof item?.text !== undefined;

  if (isSimple) res = item;

  if (isComplex) {
    res = item ? (item as DropdownItemType)[contentType] : undefined;
  }

  if (res === undefined) return res;

  return contentType === 'value' ? res : res.toString();
};

export default function Dropdown({
  items,
  darkBg = false,
  disabled = false,
  label = '',
  labelPosition = 'top',
  value,
  hasInfoIcon = false,
  infoText = '',
  hasGreyBg = false,
  context,
  onChange,
  whiteLabel,
  tradeInWidget = false,
}: DropdownProps) {
  const [selectedIndex, setSelectedIndex] = useState<number>(
    value ? getIndexForValue(items, value) : 0
  );

  useEffect(() => {
    if (items.length > 0) {
      setSelectedIndex(value ? getIndexForValue(items, value) : 0);
    }
  }, [items, value]);

  const className = {
    dropdownWrapper: clsx(
      'flex',
      labelPosition === 'left' && 'flex-row	items-center',
      labelPosition === 'top' && 'flex-col'
    ),
    headlineWrapper: clsx(
      'flex flex-none items-center mb-5px',
      labelPosition === 'left' && 'mr-20px !mb-0',
      context === 'horizontal-toolbar' &&
        'mr-10px lg:max-w-[9vw] lg:text-center'
    ),
    dropdownLabel: clsx(
      labelPosition === 'left' && 'mb-0',
      hasInfoIcon && 'mr-[11px]',
      disabled && darkBg && 'text-white',
      darkBg && 'text-white',
      !darkBg && 'text-grey80'
    ),
    dropdownBtn: clsx(
      'flex flex-1 h-[50px] items-center justify-between px-20px relative ring-1 ring-inset rounded-default w-full hover:ring-[3px]',
      disabled &&
        darkBg &&
        'ring-functional05 monochrome:ring-grey90 hover:ring-1',
      disabled && !darkBg && 'ring-grey40 monochrome:ring-grey60 hover:ring-1',
      darkBg && !disabled && 'ring-white bg-purple80 monochrome:bg-grey90',
      !darkBg && !disabled && 'ring-purple80 bg-white monochrome:ring-grey90',
      hasGreyBg && 'bg-grey10',
      tradeInWidget && 'ring-black'
    ),
    dropdownText: clsx(
      'block font-weight-bold truncate',
      disabled && darkBg && 'text-functional05 monochrome:text-grey90',
      disabled && !darkBg && 'text-grey50',
      darkBg && 'text-white',
      !darkBg && 'text-purple80 monochrome:text-grey90',
      tradeInWidget && 'text-black'
    ),
    arrowDownWrapper: clsx(
      'flex inset-y-0 items-center pointer-events-none right-0',
      context === 'horizontal-toolbar' ? 'ml-20px' : 'ml-[24px]'
    ),
    arrowDown: clsx(
      disabled &&
        darkBg &&
        'stroke-functional05 fill-functional05 monochrome:stroke-grey100 monochrome:fill-grey90',
      disabled && !darkBg && 'stroke-grey50 fill-grey50',
      darkBg && !disabled && 'stroke-white fill-white',
      !darkBg &&
        !disabled &&
        'stroke-purple80 fill-purple80 monochrome:stroke-grey70 monochrome:fill-grey70',
      tradeInWidget && 'stroke-black fill-black'
    ),
    optionsWrapper: clsx(
      '!border-3px !border-t-1px !outline-none absolute max-h-[402px] overflow-auto pb-10px pt-10px rounded-b-default rounded-md shadow-none text-base top-[49px] transition-none w-full z-10',
      context === 'financing-form' && 'max-h-[184px]',
      context === 'leasing' && 'max-h-[184px]',
      darkBg && 'border-white bg-purple80 monochrome:bg-grey90',
      !darkBg && 'border-purple80 bg-white monochrome:border-grey90',
      hasGreyBg && 'bg-grey10',
      tradeInWidget && 'border-black bg-white'
    ),
    optionWrapper: clsx(
      'cursor-pointer px-20px py-10px relative select-none hover:bg-green60',
      'monochrome:hover:bg-grey40',
      darkBg && 'text-white',
      !darkBg && 'text-purple80 monochrome:text-black',
      tradeInWidget && 'text-black hover:text-white bg-white hover:bg-grey10'
    ),
  };

  const {
    dropdownWrapper,
    dropdownLabel,
    dropdownBtn,
    dropdownText,
    arrowDownWrapper,
    arrowDown,
    optionsWrapper,
    optionWrapper,
    headlineWrapper,
  } = className;

  const selectHandler = (index: number) => {
    onChange &&
      onChange({
        selectedIndex: index,
        value: getItemContent(items[index], 'value'),
        text: getItemContent(items[index], 'text'),
      });
    setSelectedIndex(index);
  };

  return (
    <Listbox
      disabled={disabled || items.length === 0}
      value={selectedIndex}
      onChange={selectHandler}
    >
      {({ open }) => (
        <div className={dropdownWrapper}>
          {label && (
            <div className={headlineWrapper}>
              <Listbox.Label className={dropdownLabel}>{label}</Listbox.Label>
              {hasInfoIcon && <InfoIconBox text={infoText} darkBg={darkBg} />}
            </div>
          )}
          <div
            className={clsx(
              'relative w-full',
              context === 'horizontal-toolbar' && 'max-w-[162px]'
            )}
          >
            <Listbox.Button
              className={clsx(
                dropdownBtn,
                open &&
                  "ring-[3px] ring-inset rounded-b-none after:absolute after:bottom-[1px] after:content-[''] after:h-[3px] after:left-3px after:w-[calc(100%-6px)]",
                open && darkBg && 'after:bg-purple80',
                open && !darkBg && 'after:bg-white',
                open && hasGreyBg && 'after:bg-grey10'
              )}
            >
              <span className={dropdownText}>
                {getItemContent(items[(selectedIndex as number) || 0], 'text')}
              </span>

              <span className={arrowDownWrapper}>
                <ChevronDown20
                  className={clsx(arrowDown, open && 'rotate-180')}
                  aria-hidden="true"
                />
              </span>
            </Listbox.Button>

            {open && items && items.length && (
              <Listbox.Options
                className={clsx(
                  optionsWrapper,
                  whiteLabel && '1px solid black'
                )}
                static
              >
                {items.map((item, index) => {
                  const itemValue = getItemContent(item, 'value');
                  const itemText = getItemContent(item, 'text');

                  return (
                    <Listbox.Option
                      key={itemValue}
                      className={({ active }) =>
                        clsx(
                          optionWrapper,
                          active && darkBg && 'hover:text-purple80',
                          active && tradeInWidget && 'hover:text-black'
                        )
                      }
                      value={index}
                      title={(itemText ? itemText : itemValue) as string}
                    >
                      {({ selected }) => (
                        <span className={clsx(selected && 'font-weight-bold')}>
                          {itemText}
                        </span>
                      )}
                    </Listbox.Option>
                  );
                })}
              </Listbox.Options>
            )}
          </div>
        </div>
      )}
    </Listbox>
  );
}
